import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Form,
  FormGroup,
  CardHeader,
} from "reactstrap";
import {
  FaImage,
  FaPhoneAlt,
  FaEnvelope,
  FaBirthdayCake,
  FaBookOpen,
  FaGraduationCap,
  FaBuilding,
  FaUniversity,
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaLink,
  FaUser,
  FaIdBadge,
  FaDownload,
  FaFileUpload,
} from "react-icons/fa";
import PageLayout from "../PageLayout";
import { useNavigate } from "react-router-dom";
import { updateAuthorProfileApi, getAuthorDetailsApi } from "src/network/api";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import StateSelector from "./StateSelector";
import CountrySelector from "./CountrySelector";

type FormValues = {
  id: string;
  profile_image: null | File;
  doc1: null | File;
  doc2: null | File;
  doc3: null | File;
  name: string;
  contact: string;
  designation: string;
  maximum_qualification: string;
  department: string;
  university_name: string;
  country: string;
  state: string;
  profile_link: string;
  research_area: string;
  dob: string;
  about: string;
  type: string;
};

const EditAuthorProfile = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [country, setCountry] = useState("0");
  const [files, setFiles] = useState({ doc1: null, doc2: null, doc3: null });
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      contact: "",
      designation: "",
      maximum_qualification: "",
      department: "",
      university_name: "",
      country: "",
      state: "",
      profile_link: "",
      profile_image: null,
      research_area: "",
      dob: "",
      about: "",
      type: "",
    },
  });

  const type = watch("type");

  useEffect(() => {
    const fetchAuthorDetails = async () => {
      try {
        const response = await getAuthorDetailsApi();
        const details = response.data;

        (Object.keys(details) as Array<keyof FormValues>).forEach((key) => {
          if (key in details) {
            if (key == "profile_image") return;
            setValue(key, details[key]);
          }
        });

        setFiles({
          doc1: details.doc1 || null,
          doc2: details.doc2 || null,
          doc3: details.doc3 || null,
        });
      } catch (error) {
        console.error("Error fetching author details:", error);
      }
    };

    fetchAuthorDetails();
  }, [setValue]);

  const onSubmit = async (formValues: FormValues) => {
    setSaving(true);

    try {
      let formData = new FormData();
      for (let key in formValues) {
        if (
          formValues[key] !== null &&
          key !== "profile_image" &&
          key !== "doc1" &&
          key !== "doc2" &&
          key !== "doc3"
        ) {
          formData.append(key, formValues[key]);
        }
      }

      if (formValues.profile_image) {
        formData.append("profile_image", formValues.profile_image);
      }

      if (files.doc1) formData.append("doc1", files.doc1);
      if (files.doc2) formData.append("doc2", files.doc2);
      if (files.doc3) formData.append("doc3", files.doc3);

      const response = await updateAuthorProfileApi(formValues.id, formData);

      if (response.status == 200) {
        toast.success("Profile updated successfully!");
        setSaving(false);
        navigate("/");
      } else {
        toast.error(
          response.data?.message ||
            "Failed to update profile. Please try again."
        );
        setSaving(false);
      }
    } catch (e) {
      const errorMessage =
        e?.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
      setSaving(false);
    }
  };

  const handleFileChange = (e, docKey) => {
    const file = e.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [docKey]: file }));
  };

  const handleDownload = (docUrl, docKey) => {
    if (docUrl) {
      const fileExtension = docUrl.split(".").pop();
      const a = document.createElement("a");
      a.href = docUrl;
      a.download = `verification-${docKey}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      toast.error("File not available for download");
    }
  };

  return (
    <PageLayout>
      <Container fluid className=" mb-5">
        <Row className="justify-content-center">
          <Col md="10">
            <Card className="shadow mt-5" style={{ borderRadius: "10px" }}>
              <CardHeader
                className="bg-primary text-white w-100 m-0 p-3"
                style={{
                  borderRadius: "10px 10px 0 0",
                }}
              >
                <h3 className="text-white text-center">Edit Profile</h3>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row>
                    <Col md="6">
                      {/* Name */}
                      <FormGroup>
                        <Label for="name">
                          <FaUser /> Name
                        </Label>
                        <Controller
                          name="name"
                          control={control}
                          rules={{
                            required: "Name is required",
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="name"
                              placeholder="Enter your name"
                              invalid={!!errors.name}
                            />
                          )}
                        />
                        {errors.name &&
                          typeof errors.name.message === "string" && (
                            <div className="error-text">
                              {errors.name.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="designation">
                          <FaIdBadge /> Designation
                        </Label>
                        <Controller
                          name="designation"
                          control={control}
                          rules={{
                            required: "This is a required field",
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="select"
                              id="designation"
                              name="designation"
                              invalid={!!errors.designation}
                              style={
                                !!errors.designation
                                  ? {
                                      border: "1px solid red",
                                    }
                                  : {}
                              }
                            >
                              <option value="">Select</option>
                              <option value="Professor">Professor</option>
                              <option value="Scientist">Scientist</option>
                              <option value="Associate Professor">
                                Associate Professor
                              </option>
                              <option value="Assistant Professor">
                                Assistant Professor
                              </option>
                              <option value="Lecturer">Lecturer</option>
                              <option value="Instructor">Instructor</option>
                              <option value="Scholar">Scholar</option>
                            </Input>
                          )}
                        />
                        {errors.designation &&
                          typeof errors.designation.message === "string" && (
                            <div className="error-text">
                              {errors.designation.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="maximum_qualification">
                          <FaGraduationCap /> Maximum Qualification Completed
                        </Label>
                        <Controller
                          name="maximum_qualification"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="select"
                              id="maximum_qualification"
                              name="maximum_qualification"
                              invalid={!!errors.maximum_qualification}
                            >
                              <option value="">Select</option>
                              <option value="PostDoc">PostDoc</option>
                              <option value="Ph.D">Ph.D</option>
                              <option value="Masters">Masters</option>
                              <option value="Bachelor">Bachelor</option>
                            </Input>
                          )}
                        />
                        {errors.maximum_qualification &&
                          typeof errors.maximum_qualification.message ===
                            "string" && (
                            <div className="error-text">
                              {errors.maximum_qualification.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="department">
                          <FaBuilding /> Department/College
                        </Label>
                        <Controller
                          name="department"
                          control={control}
                          rules={{
                            required: "This is a required field",
                            minLength: {
                              value: 3,
                              message:
                                "Department name must be at least 3 characters",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="department"
                              placeholder="Enter your department"
                              invalid={!!errors.department}
                            />
                          )}
                        />
                        {errors.department &&
                          typeof errors.department.message === "string" && (
                            <div className="error-text">
                              {errors.department.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="university_name">
                          <FaUniversity /> University/Organization
                        </Label>
                        <Controller
                          name="university_name"
                          control={control}
                          rules={{
                            minLength: {
                              value: 3,
                              message:
                                "University/Organization name must be at least 3 characters",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="university_name"
                              placeholder="Enter your university/organization"
                              invalid={!!errors.university_name}
                            />
                          )}
                        />
                        {errors.university_name &&
                          typeof errors.university_name.message ===
                            "string" && (
                            <div className="error-text">
                              {errors.university_name.message}
                            </div>
                          )}
                      </FormGroup>
                      <FormGroup>
                        <Label for="country">
                          <FaGlobeAmericas /> Country
                        </Label>
                        <Controller
                          name="country"
                          control={control}
                          rules={{
                            required: "Country is required",
                          }}
                          render={({ field }) => (
                            <CountrySelector
                              currentCountry={field.value}
                              onSelectCountryCallback={(value) => {
                                field.onChange(value);
                                setCountry(value);
                              }}
                            />
                          )}
                        />
                        {errors.country &&
                          typeof errors.country.message === "string" && (
                            <div className="error-text">
                              {errors.country.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="state">
                          <FaMapMarkerAlt /> State
                        </Label>
                        <Controller
                          name="state"
                          control={control}
                          rules={{
                            required: "State is required",
                          }}
                          render={({ field }) => (
                            <StateSelector
                              currentState={field.value}
                              countryId={getValues("country")}
                              onSelectStateCallback={(selectedState: number) =>
                                setValue("state", selectedState.toString())
                              }
                            />
                          )}
                        />
                        {errors.state &&
                          typeof errors.state.message === "string" && (
                            <div className="error-text">
                              {errors.state.message}
                            </div>
                          )}
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label for="profile_link">
                          <FaLink /> Profile Link
                        </Label>
                        <Controller
                          name="profile_link"
                          control={control}
                          rules={{
                            pattern: {
                              value: /^(ftp|http|https):\/\/[^ "]+$/,
                              message: "Please enter a valid URL",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="url"
                              id="profile_link"
                              placeholder="Enter your profile link"
                              invalid={!!errors.profile_link}
                            />
                          )}
                        />
                        {errors.profile_link &&
                          typeof errors.profile_link.message === "string" && (
                            <div className="error-text">
                              {errors.profile_link.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="research_area">
                          <FaBookOpen /> Research Area
                        </Label>
                        <Controller
                          name="research_area"
                          control={control}
                          rules={{
                            required: "This area is a required",
                            minLength: {
                              value: 3,
                              message:
                                "Research area must be at least 3 characters",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="text"
                              id="research_area"
                              placeholder="Enter your research area"
                              invalid={!!errors.research_area}
                            />
                          )}
                        />
                        {errors.research_area &&
                          typeof errors.research_area.message === "string" && (
                            <div className="error-text">
                              {errors.research_area.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="contact">
                          <FaPhoneAlt /> Phone
                        </Label>
                        <Controller
                          name="contact"
                          control={control}
                          rules={{
                            // required: "Phone number is required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Phone number must be 10 digits",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="tel"
                              id="contact"
                              placeholder="Enter your phone number"
                              invalid={!!errors.contact}
                            />
                          )}
                        />
                        {errors.contact &&
                          typeof errors.contact.message === "string" && (
                            <div className="error-text">
                              {errors.contact.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="dob">
                          <FaBirthdayCake /> Date of Birth
                        </Label>
                        <Controller
                          name="dob"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="date"
                              id="dob"
                              name="dob"
                              invalid={!!errors.dob}
                              placeholder="yyyy-mm-dd"
                            />
                          )}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label for="about">About</Label>
                        <Controller
                          name="about"
                          control={control}
                          rules={{
                            minLength: {
                              value: 10,
                              message: "About must be at least 10 characters",
                            },
                            maxLength: {
                              value: 500,
                              message:
                                "About must be no longer than 1000 characters",
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="textarea"
                              id="about"
                              name="about"
                              invalid={!!errors.about}
                              rows="4"
                            />
                          )}
                        />
                        {errors.about &&
                          typeof errors.about.message === "string" && (
                            <div className="error-text">
                              {errors.about.message}
                            </div>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <Label for="profile_image">
                          <FaImage /> Profile Image
                        </Label>

                        <Input
                          type="file"
                          id="profile_image"
                          name="profile_image"
                          className="form-control"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setValue("profile_image", file);
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {type !== "author" && (
                    <>
                      <hr />
                      <div>
                        <h4 className="text-center text-primary">
                          Verification Documents
                        </h4>

                        {["doc1", "doc2", "doc3"].map((docKey, index) => (
                          <FormGroup key={docKey} className="position-relative">
                            <Label for={docKey}>
                              <FaFileUpload className="mr-1" /> Document{" "}
                              {index + 1}
                            </Label>
                            <div className="input-group">
                              <Input
                                type="file"
                                id={docKey}
                                name={docKey}
                                className="form-control"
                                onChange={(e) => handleFileChange(e, docKey)}
                              />
                              {files[docKey] &&
                                typeof files[docKey] === "string" &&
                                !files[docKey].includes("no-user") && (
                                  <Button
                                    color="success"
                                    className="download-btn"
                                    onClick={() =>
                                      handleDownload(files[docKey], docKey)
                                    }
                                  >
                                    <FaDownload />
                                  </Button>
                                )}
                            </div>
                          </FormGroup>
                        ))}
                      </div>
                    </>
                  )}

                  <Button
                    className="btn-block"
                    color="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? <>Saving Profile...</> : "Save Changes"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageLayout>
  );
};

export default EditAuthorProfile;
